import { z } from 'zod';
import {
  audienceSchema,
  campaignSpecSchema,
  NOT_READY_REASON_VALUES,
  OPTOUT_SCOPE,
  OPTOUT_SOURCE,
  OUTBOUND_CALL_OUTCOME_VALUES,
  OUTREACH_SKIP_REASON,
  OUTREACH_TERMINAL_REASON,
  QUEUE_LANE_VALUES,
  SUBJECT_KINDS,
  type NotReadyReason,
  type OptoutSource,
  type OutreachSkipReason,
  type OutreachTerminalReason,
} from '@vagusx/shared';
import { batchProviderRankingSchema } from '../appointment-type-matching/types';

/**
 * The outreach HTTP contract, shared by the API and the web app (ADR-154).
 *
 * `campaignSpecSchema` is reused verbatim rather than restated. The spec is the
 * runtime source of truth — the driver, the compiler, and the editor all have
 * to mean the same thing by it, and a hand-written copy here would be a fourth
 * definition free to drift from the three that matter.
 */

// ───────────────────────────── campaigns ─────────────────────────────

export const campaignStatusSchema = z.enum(['draft', 'active', 'paused']);

export const outreachCampaignSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  status: campaignStatusSchema,
  priority: z.number().int(),
  endWhenNoLongerQualifies: z.boolean(),
  activeVersionId: z.string().uuid().nullable(),
  /** The live spec, if one has been published. Draft campaigns have none. */
  spec: campaignSpecSchema.nullable(),
  /** Plain-language read-back of the audience — the campaign list's subtitle. */
  audienceDescription: z.string().nullable(),
  version: z.number().int().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Of the runs that started in the practice's last 30 days, how many there
   * were and how many met the goal because of the campaign (ADR-318, H).
   * Sent by the list only, for campaigns that have published; one grouped
   * read for the whole list.
   */
  last30Days: z.object({ joined: z.number().int(), succeeded: z.number().int() }).optional(),
  /**
   * Per voice step, the workflow version it pinned and the newest one published
   * (ADR-318, R10), so the editor can say "a newer version is published". Sent
   * by `GET /:id` only; absent on the list.
   */
  workflowPins: z
    .array(
      z.object({
        stepId: z.string(),
        pinnedVersion: z.number().int(),
        /** Null when the workflow has since been archived or unpublished. */
        latestVersion: z.number().int().nullable(),
      })
    )
    .optional(),
});
export type OutreachCampaign = z.infer<typeof outreachCampaignSchema>;
export type WorkflowPin = NonNullable<OutreachCampaign['workflowPins']>[number];

export const createCampaignSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  priority: z.number().int().min(0).max(100).optional(),
});
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

export const updateCampaignSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(500).nullable().optional(),
  priority: z.number().int().min(0).max(100).optional(),
  // No `status` (ADR-318, R4): starting and stopping a campaign is
  // `POST /:id/activate` / `/pause`, under `outreach:run`. A `status` sent here
  // is stripped, never applied.
  endWhenNoLongerQualifies: z.boolean().optional(),
});
export type UpdateCampaignInput = z.infer<typeof updateCampaignSchema>;

/**
 * Publishing creates an immutable version and points the campaign at it.
 *
 * A run pins the version it started on, so publishing never changes the
 * sequence under a patient who is already three steps in.
 */
export const publishCampaignSchema = z.object({
  spec: campaignSpecSchema,
  label: z.string().max(80).nullable().optional(),
  /** Provenance when the spec came from the compiler. Never read at runtime. */
  sourcePrompt: z.string().max(4000).nullable().optional(),
});
export type PublishCampaignInput = z.infer<typeof publishCampaignSchema>;

export const campaignVersionSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  label: z.string().nullable(),
  /**
   * Nullable because a version published under an older shape may no longer
   * parse. The service returns null for those on purpose — a stale version
   * must render as unreadable rather than taking the whole history down, which
   * is exactly the page an author needs in order to roll back to a good one.
   */
  spec: campaignSpecSchema.nullable(),
  editedAfterGeneration: z.boolean(),
  createdAt: z.string(),
  /** How many runs are still on this version. */
  liveRuns: z.number().int(),
});
export type CampaignVersion = z.infer<typeof campaignVersionSchema>;

// ───────────────────────────── audience preview ─────────────────────────────

export const previewAudienceSchema = z.object({
  audience: audienceSchema,
  /**
   * The campaign this audience belongs to has a call step. The server applies
   * what that means — a date of birth is required whatever the audience's
   * switch says (ADR-333) — so a caller states the fact, not the consequence.
   */
  placesCalls: z.boolean().default(false),
});
export type PreviewAudienceInput = z.infer<typeof previewAudienceSchema>;

export const audiencePreviewSchema = z.object({
  /** Distinct patients who match right now. */
  patients: z.number().int(),
  /** Match rows, which exceeds `patients` when someone matches twice. */
  matched: z.number().int(),
  /**
   * True when the audience exceeds the reconciler's ceiling and the campaign
   * would be refused on every tick. Must be surfaced — a preview that says
   * "142,000 match" while nothing ever runs is worse than no preview.
   */
  aboveCeiling: z.boolean(),
  description: z.string(),
  /** Filters this audience cannot answer. Non-empty means publishing fails. */
  problems: z.array(z.string()),
  /**
   * Of `patients`, how many are ready to call and how many are not, and why
   * (ADR-318, P5). Referral audiences only; OPTIONAL until task B fills them,
   * so a server without readiness still answers.
   */
  ready: z.number().int().optional(),
  notReady: z.number().int().optional(),
  notReadyReasons: z
    .array(z.object({ reason: z.enum(NOT_READY_REASON_VALUES), count: z.number().int() }))
    .optional(),
});
export type AudiencePreview = z.infer<typeof audiencePreviewSchema>;

// ───────────────────────────── simulation ─────────────────────────────

export const simulateCampaignSchema = z.object({
  spec: campaignSpecSchema,
  /** What every simulated call does. The useful case is one that never connects. */
  outcome: z.string().default('no_answer'),
  /** Defaults to the organization's only location, or the one supplied. */
  locationId: z.string().uuid().nullable().optional(),
  startAt: z.string().datetime().nullable().optional(),
});
export type SimulateCampaignInput = z.infer<typeof simulateCampaignSchema>;

export const simulatedAttemptSchema = z.object({
  at: z.string(),
  stepId: z.string(),
  stepLabel: z.string().nullable(),
  stepRun: z.number().int(),
  stepRunsTotal: z.number().int(),
  isCallback: z.boolean(),
  leavesMessage: z.boolean(),
});

export const simulationResultSchema = z.object({
  attempts: z.array(simulatedAttemptSchema),
  endedBy: z.object({ reason: z.string() }).nullable(),
  timezone: z.string(),
  /**
   * What the preview cannot know. Rendered as a caveat rather than buried:
   * the gates may push any of these later, and rotation times are only
   * representative until a real run exists.
   */
  caveats: z.array(z.string()),
});
export type SimulationResult = z.infer<typeof simulationResultSchema>;

// ───────────────────────────── compile ─────────────────────────────

/**
 * Rebuild a campaign from its description.
 *
 * Both halves are required. The spec is not a starting point the caller could
 * omit — it is what the edit is applied TO, and it is what keeps settings no
 * sentence mentions (message slots, per-step windows) from being dropped on
 * every round trip. A compile against no spec would be a different, lossier
 * feature.
 */
export const compileCampaignSchema = z.object({
  spec: campaignSpecSchema,
  text: z.string().min(1).max(8000),
});
export type CompileCampaignInput = z.infer<typeof compileCampaignSchema>;

export const campaignChangeSchema = z.object({
  /** The sentence id, so the editor can open the control that moved. */
  id: z.string(),
  section: z.enum(['audience', 'sequence', 'ending', 'hours']),
  kind: z.enum(['added', 'removed', 'changed']),
  before: z.string().optional(),
  after: z.string().optional(),
});
export type CampaignChangeDto = z.infer<typeof campaignChangeSchema>;

export const compileResultSchema = z.object({
  spec: campaignSpecSchema,
  /**
   * Everything that moved, phrased in the sentences the author reads — including
   * what they did not ask for. This is what makes applying a compiled edit
   * directly safe enough to do without a blocking review.
   */
  changes: z.array(campaignChangeSchema),
  /** What could not be applied: names that did not resolve, unknown outcomes. */
  warnings: z.array(z.string()),
});
export type CompileResult = z.infer<typeof compileResultSchema>;

// ───────────────────────────── contact policy ─────────────────────────────

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:mm');

export const contactPolicySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  enabled: z.boolean(),
  quietHoursStart: hhmm,
  quietHoursEnd: hhmm,
  contactCooldownMinutes: z.number().int(),
  maxContactsPerPatient: z.number().int(),
  maxContactsPerDays: z.number().int(),
  maxConcurrentCalls: z.number().int(),
  dailyCallCap: z.number().int(),
  voicemailGraceMinutes: z.number().int(),
  attributionWindowDays: z.number().int(),
  reEntryCooldownDays: z.number().int(),
  maxCallbackHonors: z.number().int(),
  defaultFromPhone: z.string().nullable(),
  defaultAgentId: z.string().uuid().nullable(),
  /**
   * The code-enforced floor. Sent so the UI can render it as locked rather
   * than guessing — an organization may narrow it, never widen it.
   */
  quietHoursFloor: z.object({ start: hhmm, end: hhmm }),
  updatedAt: z.string(),
});
export type ContactPolicy = z.infer<typeof contactPolicySchema>;

export const updateContactPolicySchema = z.object({
  enabled: z.boolean().optional(),
  quietHoursStart: hhmm.optional(),
  quietHoursEnd: hhmm.optional(),
  contactCooldownMinutes: z.number().int().min(0).max(20160).optional(),
  maxContactsPerPatient: z.number().int().min(1).max(50).optional(),
  maxContactsPerDays: z.number().int().min(1).max(365).optional(),
  maxConcurrentCalls: z.number().int().min(1).max(100).optional(),
  dailyCallCap: z.number().int().min(1).max(10000).optional(),
  voicemailGraceMinutes: z.number().int().min(0).max(10080).optional(),
  attributionWindowDays: z.number().int().min(1).max(90).optional(),
  reEntryCooldownDays: z.number().int().min(0).max(365).optional(),
  maxCallbackHonors: z.number().int().min(0).max(20).optional(),
  defaultFromPhone: z.string().nullable().optional(),
  defaultAgentId: z.string().uuid().nullable().optional(),
});
export type UpdateContactPolicyInput = z.infer<typeof updateContactPolicySchema>;

// ───────────────────────────── the catalog ─────────────────────────────

/**
 * What the condition picker renders. Served rather than hardcoded in the web
 * app so the catalog has exactly one definition — adding an audience must not
 * mean editing a list in the frontend too.
 */
export const audienceCatalogEntrySchema = z.object({
  type: z.string(),
  supportedFilters: z.array(z.enum(['location', 'provider', 'appointmentType'])),
  subjectKind: z.enum(['appointment', 'referral']).nullable(),
  recheckable: z.boolean(),
});
export type AudienceCatalogEntry = z.infer<typeof audienceCatalogEntrySchema>;

// ═══════════════════════ Campaign auto-dial (ADR-318) ═══════════════════════
//
// The wire shapes tasks F, G and H build against (docs/outreach-autodial-plan.md
// §6.2). Frozen by W0: a change here is a contract amendment.

const isoDateTime = z.string().datetime({ offset: true });

// ───────────────────────────── launch check ─────────────────────────────

/**
 * What the launch check can say, as a closed list so `activate`'s 422 can name
 * the checks that failed and the editor can point at the control to fix.
 */
export const LAUNCH_CHECK_CODES = [
  /** A filter the audience cannot answer. */
  'audience_filters',
  /** More matches than the reconciler will take. */
  'audience_above_ceiling',
  /** Nobody is ready to call right now (a warning: they may be soon). */
  'audience_none_ready',
  /** A voice step names no workflow. */
  'workflow_missing',
  /** A step's workflow is not published, not outbound voice, or not for this audience. */
  'workflow_unavailable',
  /** The campaign's persona is not in the catalog. */
  'persona_invalid',
  /** A step's agent is not in the catalog, or cannot speak as the persona. */
  'agent_invalid',
  /** A step's `timing.days` names a day the campaign does not call on. */
  'step_days_outside_window',
  /** A step's custom `timing.hours` never meet the calling hours. */
  'step_hours_outside_window',
  /** The calling hours leave no time once the quiet-hours floor and policy apply. */
  'window_empty',
  /** A step uses a channel campaigns cannot send yet (SMS). */
  'channel_unsupported',
  /** The organization's contact policy is switched off, so nothing would dial. */
  'contact_policy_disabled',
] as const;
export type LaunchCheckCode = (typeof LAUNCH_CHECK_CODES)[number];

export const launchCheckSchema = z.object({
  code: z.enum(LAUNCH_CHECK_CODES),
  level: z.enum(['pass', 'warn', 'fail']),
  message: z.string(),
  detail: z.string().optional(),
  /** The step the check is about, when it is about one. */
  stepId: z.string().optional(),
});
export type LaunchCheck = z.infer<typeof launchCheckSchema>;

export const launchReadinessSchema = z.object({
  /** True when no check failed. Warnings do not block. */
  ok: z.boolean(),
  checks: z.array(launchCheckSchema),
});
export type LaunchReadiness = z.infer<typeof launchReadinessSchema>;

/**
 * Where `POST /:id/activate`'s refusal carries the checks: the 422's
 * `details.launchReadiness`. The server throws
 * `ValidationError.operationNotAllowed(...).withDetails({ launchReadiness })`;
 * the editor reads `activateRefusalDetailsSchema.safeParse(err.details)` off the
 * `HttpError`.
 */
export const activateRefusalDetailsSchema = z.object({ launchReadiness: launchReadinessSchema });
export type ActivateRefusalDetails = z.infer<typeof activateRefusalDetailsSchema>;

/** The editor has no stored draft (publish is the save), so the spec travels in the body (R3). */
export const launchCheckInputSchema = z.object({ spec: campaignSpecSchema });
export type LaunchCheckInput = z.infer<typeof launchCheckInputSchema>;

// ───────────────────────────── workflow options ─────────────────────────────

/** A published, outbound, voice workflow a campaign step may run (P3). */
export const workflowOptionSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  /** Null for a workflow only campaigns use. */
  intent: z.string().nullable(),
  activeVersion: z.object({
    id: z.string().uuid(),
    version: z.number().int(),
    publishedAt: isoDateTime,
  }),
});
export type WorkflowOption = z.infer<typeof workflowOptionSchema>;

// ───────────────────────────── queue ─────────────────────────────

const queueLaneSchema = z.enum(QUEUE_LANE_VALUES);

/**
 * Why a row is where it is. A closed union of the not-ready reasons, the skip
 * reasons and the terminal reasons (R12), so the UI maps codes to copy and
 * never parses text.
 */
export type QueueReasonCode = NotReadyReason | OutreachSkipReason | OutreachTerminalReason;
// Typed as the union, not `string`, so the UI can hold an exhaustive
// `Record<QueueReasonCode, string>` and a new reason fails typecheck there.
export const QUEUE_REASON_CODES = [
  ...new Set<QueueReasonCode>([
    ...NOT_READY_REASON_VALUES,
    ...Object.values(OUTREACH_SKIP_REASON),
    ...Object.values(OUTREACH_TERMINAL_REASON),
  ]),
] as [QueueReasonCode, ...QueueReasonCode[]];
const TERMINAL_REASON_VALUES = Object.values(OUTREACH_TERMINAL_REASON) as [
  OutreachTerminalReason,
  ...OutreachTerminalReason[],
];
const OPTOUT_SOURCE_VALUES = Object.values(OPTOUT_SOURCE) as [OptoutSource, ...OptoutSource[]];
export const queueReasonSchema = z.object({
  code: z.enum(QUEUE_REASON_CODES),
  detail: z.string().optional(),
});

export const queueRowSchema = z.object({
  /**
   * Stable row key: the run id, or `<lane>:<subjectId>` for a `ready` or
   * `not_ready` row, which has no run (`<lane>:<patientId>` when the audience
   * has no subject).
   */
  key: z.string(),
  lane: queueLaneSchema,
  /** PHI: `outreach:view` only, never logged. */
  patient: z.object({ id: z.string().uuid(), name: z.string() }),
  subject: z
    .object({
      kind: z.enum(SUBJECT_KINDS),
      id: z.string().uuid(),
      label: z.string(),
      /**
       * The task that owns a referral, which is what the referral panel opens
       * on. Null for an appointment, or a referral no task points at.
       */
      taskId: z.string().uuid().nullable(),
    })
    .nullable(),
  step: z
    .object({
      id: z.string(),
      label: z.string(),
      /**
       * The step of the latest try placed, not the next one: the driver
       * decides the next step at its next tick. `run` is that try's 1-based
       * number within the step (1 before any), `of` how many the step has.
       */
      run: z.number().int(),
      of: z.number().int(),
    })
    .nullable(),
  nextAt: isoDateTime.nullable(),
  reason: queueReasonSchema.nullable(),
  /** Null for `ready` and `not_ready`, the two lanes with no run. */
  runId: z.string().uuid().nullable(),
  interactionId: z.string().uuid().nullable(),
  /**
   * `ready` rows of a referral audience only: the referral's ranked provider
   * pool, the one readiness judged it on (the Appointment type and Provider
   * columns). Sent with the row so the lane needs no second ranking request.
   */
  ranking: batchProviderRankingSchema.optional(),
});
export type QueueRow = z.infer<typeof queueRowSchema>;

export const queuePageSchema = z.object({
  rows: z.array(queueRowSchema),
  nextCursor: z.string().nullable(),
});
export type QueuePage = z.infer<typeof queuePageSchema>;

/**
 * `GET /:id/queue` query. A cursor for run lanes, an offset (as the cursor)
 * for `ready` and `not_ready`, which are computed lists.
 */
export const queueQuerySchema = z.object({
  lane: queueLaneSchema,
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type QueueQuery = z.infer<typeof queueQuerySchema>;

export const queueSummarySchema = z.object({
  lanes: z.array(z.object({ lane: queueLaneSchema, count: z.number().int() })),
  /**
   * When membership and readiness were last evaluated. Null when they could
   * not be (the audience grew past the ceiling, say): `ready` and
   * `not_ready` are then absent from `lanes`, and no freshness is claimed.
   */
  checkedAt: isoDateTime.nullable(),
});
export type QueueSummary = z.infer<typeof queueSummarySchema>;

// ───────────────────────────── results ─────────────────────────────

/**
 * `GET /:id/results` query: ISO dates, range at most 366 days. Both or
 * neither: with neither, the server reads the practice's last 30 days in the
 * practice's own timezone (the list's figure), and answers with that range.
 */
export const campaignResultsQuerySchema = z
  .object({ from: z.string().date().optional(), to: z.string().date().optional() })
  .refine((q) => (q.from === undefined) === (q.to === undefined), {
    message: 'Give both `from` and `to`, or neither',
    path: ['to'],
  })
  .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: '`from` must not be after `to`',
    path: ['from'],
  })
  .refine(
    (q) => !q.from || !q.to || Date.parse(q.to) - Date.parse(q.from) <= 366 * 24 * 60 * 60 * 1000,
    { message: 'The range may be at most 366 days', path: ['to'] }
  );
export type CampaignResultsQuery = z.infer<typeof campaignResultsQuerySchema>;

/** Counts only, no patient data. Runs are counted by start date inside the range. */
export const campaignResultsSchema = z.object({
  range: z.object({ from: z.string().date(), to: z.string().date() }),
  funnel: z.object({
    qualified: z.number().int(),
    contacted: z.number().int(),
    reached: z.number().int(),
    succeeded: z.number().int(),
  }),
  endedBy: z.array(
    z.object({
      reason: z.enum(TERMINAL_REASON_VALUES),
      count: z.number().int(),
    })
  ),
  dispositions: z.array(
    z.object({ disposition: z.enum(OUTBOUND_CALL_OUTCOME_VALUES), count: z.number().int() })
  ),
  needsPerson: z.object({
    wrongNumber: z.number().int(),
    escalated: z.number().int(),
    exhausted: z.number().int(),
  }),
  volume: z.object({
    calls: z.number().int(),
    texts: z.number().int(),
    callMinutes: z.number(),
    talkMinutes: z.number(),
  }),
  byVersion: z.array(
    z.object({
      versionId: z.string().uuid(),
      version: z.number().int(),
      runs: z.number().int(),
      succeeded: z.number().int(),
    })
  ),
});
export type CampaignResults = z.infer<typeof campaignResultsSchema>;

// ───────────────────────────── exclusions ─────────────────────────────

/** `POST /:id/exclusions`: take a patient out of one campaign, with or without a run (R5). */
export const createExclusionSchema = z.object({
  patientId: z.string().uuid(),
  reason: z.string().trim().min(1).max(500),
});
export type CreateExclusionInput = z.infer<typeof createExclusionSchema>;

export const campaignExclusionSchema = z.object({
  id: z.string().uuid(),
  campaignId: z.string().uuid(),
  patientId: z.string().uuid(),
  reason: z.string().nullable(),
  createdAt: isoDateTime,
  createdBy: z.string().uuid().nullable(),
});
export type CampaignExclusion = z.infer<typeof campaignExclusionSchema>;

/** `POST` answers `{exclusion}`; `DELETE` answers `{exclusion: null}`. */
export const exclusionResponseSchema = z.object({ exclusion: campaignExclusionSchema.nullable() });
export type ExclusionResponse = z.infer<typeof exclusionResponseSchema>;

// ───────────────────────────── patient opt-outs ─────────────────────────────

const optoutScopeSchema = z.enum([OPTOUT_SCOPE.ALL, OPTOUT_SCOPE.VOICE, OPTOUT_SCOPE.SMS]);

export const patientOptoutSchema = z.object({
  id: z.string().uuid(),
  scope: optoutScopeSchema,
  source: z.enum(OPTOUT_SOURCE_VALUES),
  capturedAt: isoDateTime,
  /** Null when automation captured it (a call, an SMS keyword). */
  capturedBy: z.string().uuid().nullable(),
  revokedAt: isoDateTime.nullable(),
  /** Revocation always has a person behind it; automation never revokes. */
  revokedBy: z.string().uuid().nullable(),
});
export type PatientOptout = z.infer<typeof patientOptoutSchema>;

/**
 * `POST /api/patients/:id/optouts`: a staff-recorded opt-out.
 *
 * No free-text reason: `patient_contact_optout` deliberately has no text
 * column, because free text invites PHI into a table whose purpose is to be
 * quoted. The scope, the source (`staff`) and who recorded it are the record.
 */
export const createPatientOptoutSchema = z.object({ scope: optoutScopeSchema });
export type CreatePatientOptoutInput = z.infer<typeof createPatientOptoutSchema>;

export const patientOptoutListSchema = z.object({ optouts: z.array(patientOptoutSchema) });
export const patientOptoutResponseSchema = z.object({ optout: patientOptoutSchema });

// ───────────────────────────── recent contact (ADR-365) ─────────────────────────────

/**
 * Why the office called, as a closed vocabulary the voice agent reasons from.
 * Decided by the API from the run's audience and subject (or a manual dial's
 * intent), never from a campaign's internal name, which is staff-facing.
 */
export const RECENT_CONTACT_PURPOSES = [
  'referral',
  'appointment',
  'missed_appointment',
  'recall',
  'new_patient',
  'follow_up',
  'other',
] as const;
export type RecentContactPurpose = (typeof RECENT_CONTACT_PURPOSES)[number];

/** How many contacts the lookup returns at most, newest first. */
export const RECENT_CONTACT_MAX = 3;

/**
 * `POST /internal/outreach/recent-contact`. Keyed on `patientId`, which only a
 * VERIFIED identity produces — the voice runtime reaches this after
 * `/patients/identify` matched, never on caller ID (ADR-134). `callerLine` is
 * the number the caller rang from, used only to mark which calls went to it.
 */
export const recentContactLookupSchema = z.object({
  organizationId: z.string().uuid(),
  patientId: z.string().uuid(),
  callerLine: z.string().max(32).nullable().optional(),
});
export type RecentContactLookup = z.infer<typeof recentContactLookupSchema>;

/** One call (or text) that went out. */
export const recentContactAttemptSchema = z.object({
  at: isoDateTime,
  channel: z.string(),
  /** `outcome_category`, the system's disposition; null while unresolved. */
  outcome: z.string().nullable(),
  leftMessage: z.boolean(),
  /** Whether it went to the line they are calling from; null when that line is unknown. */
  onThisLine: z.boolean().nullable(),
});
export type RecentContactAttempt = z.infer<typeof recentContactAttemptSchema>;

/**
 * The practice-visible half of the post-call review of the contact's latest
 * evaluated call. The internal-only fields (`category_scores`, `improvements`,
 * `grade`) are never selected, the line the evaluation read boundary draws.
 */
export const recentContactEvaluationSchema = z.object({
  outcomeLabel: z.string().nullable(),
  outcomeDetail: z
    .object({
      startTime: z.string(),
      providerName: z.string().nullable(),
      locationName: z.string().nullable(),
      locationTimezone: z.string().nullable().optional(),
    })
    .nullable(),
  headline: z.string().nullable(),
  officeNote: z.string().nullable(),
  patientMisinformed: z.boolean(),
});
export type RecentContactEvaluation = z.infer<typeof recentContactEvaluationSchema>;

/** One campaign run's calls, or one manual call, to the patient. */
export const recentContactSchema = z.object({
  purpose: z.enum(RECENT_CONTACT_PURPOSES),
  /** The run, when a campaign placed it; null for a call staff placed. */
  runId: z.string().uuid().nullable(),
  /** Oldest first. */
  attempts: z.array(recentContactAttemptSchema).min(1),
  voicemailLeft: z.boolean(),
  /** The latest attempt's disposition, as its `OUTBOUND_CALL_OUTCOME_LABELS` label. */
  latestOutcome: z.string().nullable(),
  evaluation: recentContactEvaluationSchema.nullable(),
  /** Who referred them, for a referral: the provider, else the practice. */
  referrer: z.string().nullable(),
  /** The appointment a reminder or a no-show call was about. */
  appointment: z
    .object({
      startTime: isoDateTime,
      timezone: z.string().nullable(),
      providerName: z.string().nullable(),
      locationName: z.string().nullable(),
    })
    .nullable(),
});
export type RecentContact = z.infer<typeof recentContactSchema>;

export const recentContactResponseSchema = z.object({
  contacts: z.array(recentContactSchema).max(RECENT_CONTACT_MAX),
  /** How far back the lookup looked, so "nothing" can say over what span. */
  windowDays: z.number().int().positive(),
});
export type RecentContactResponse = z.infer<typeof recentContactResponseSchema>;

// ───────────────────────────── open referral (ADR-366) ─────────────────────────────

/**
 * `POST /internal/outreach/open-referral`. Keyed on `patientId`, which only a
 * VERIFIED identity produces (ADR-134): the voice agent asks after `Match`.
 */
export const openReferralLookupSchema = z.object({
  organizationId: z.string().uuid(),
  patientId: z.string().uuid(),
});
export type OpenReferralLookup = z.infer<typeof openReferralLookupSchema>;

/**
 * The referral the agent may offer to book: the one the campaign would chase
 * first. `taskId` is what a booking for it carries, so the API links the
 * appointment back onto the referral (the Referrals worklist).
 */
export const openReferralSchema = z.object({
  documentId: z.string().uuid(),
  taskId: z.string().uuid(),
  receivedAt: isoDateTime,
  /** Who referred them: the referring provider, else the practice; null when neither. */
  referrer: z.string().nullable(),
});
export type OpenReferral = z.infer<typeof openReferralSchema>;

/** `referral` is null when nothing is waiting — never a boolean flag. */
export const openReferralResponseSchema = z.object({
  referral: openReferralSchema.nullable(),
  /** Further unbooked referrals beyond this one. */
  others: z.number().int().min(0),
});
export type OpenReferralResponse = z.infer<typeof openReferralResponseSchema>;
